import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Users, UserCheck, PoundSterling, Bell, Wrench, XCircle, FileText, Clock,
  LogOut, ShieldCheck, Search, ChevronRight, Ban, RefreshCw, CheckCircle2, Trash2, Download
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAdmin } from '../../contexts/AdminContext';
import { usePlatform } from '../../contexts/PlatformContext';
import { useToast } from '../../hooks/use-toast';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell,
} from 'recharts';
import AdminAgents from '../../components/admin/AdminAgents';
import AdminEmails from '../../components/admin/AdminEmails';

const Stat = ({ label, value, icon: Icon, tone = 'bg-white text-[#1F0068]', sub }) => (
  <div className={`rounded-2xl p-5 border border-[#1F0068]/5 ${tone}`}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold opacity-80"><Icon className="w-4 h-4" /> {label}</div>
    <div className="mt-1 text-2xl md:text-3xl font-extrabold">{value}</div>
    {sub && <div className="text-xs opacity-70 mt-0.5">{sub}</div>}
  </div>
);

const AdminDashboard = () => {
  const { admin, logout } = useAdmin();
  const { customers, agents, policies, followUps, cancellations, services, setAgentStatus, removeAgent, deactivateCustomer, activateCustomer, revenue, monthlySeries } = usePlatform();
  const nav = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState('overview');
  const [q, setQ] = useState('');

  const rev = revenue();
  const series = monthlySeries();
  const pendingPayments = policies.filter((p) => ['Payment Due', 'Awaiting Payment', 'Overdue', 'Partially Paid'].includes(p.paymentStatus));
  const openServices = services.filter((s) => s.status !== 'Completed');
  const todaysQuotes = policies.filter((p) => {
    const d = new Date(p.createdAt); const now = new Date();
    return d.toDateString() === now.toDateString();
  });

  const filteredCustomers = customers.filter((c) => {
    const s = q.toLowerCase();
    if (!s) return true;
    return [c.name, c.email, c.postcode, c.mobile, c.city].some((v) => (v || '').toLowerCase().includes(s));
  });

  const exportCSV = (rows, filename) => {
    if (!rows.length) return toast({ title: 'Nothing to export' });
    const cols = Object.keys(rows[0]);
    const csv = [cols.join(','), ...rows.map((r) => cols.map((c) => JSON.stringify(r[c] ?? '')).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = filename; link.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#F5F1EA]">
      <header className="bg-[#0F0532] text-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-[#FF5C39]" />
            <div className="leading-tight"><div className="font-bold text-sm">Domestic Home Services</div><div className="text-[10px] tracking-[0.22em] text-white/60 uppercase">Admin Portal</div></div>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <div className="hidden md:block text-white/75">Signed in as <b className="text-white">{admin?.name}</b></div>
            <Button variant="ghost" onClick={() => { logout(); nav('/admin/login'); }} className="gap-1.5 text-white/85 hover:bg-white/10 rounded-full"><LogOut className="w-4 h-4" /> Sign out</Button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 flex items-center gap-1 overflow-x-auto pb-2">
          {['overview', 'customers', 'agents', 'revenue', 'services', 'cancellations', 'emails'].map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm font-semibold rounded-full whitespace-nowrap ${tab === t ? 'bg-white text-[#1F0068]' : 'text-white/75 hover:bg-white/10'}`}>{t[0].toUpperCase() + t.slice(1)}</button>
          ))}
        </div>
      </header>

      <main className="py-8">
        <div className="max-w-7xl mx-auto px-5">
          {tab === 'overview' && (
            <>
              <h1 className="text-3xl font-bold text-[#1F0068] tracking-tight mb-6">Platform overview</h1>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <Stat label="Total Customers" value={customers.length} icon={Users} />
                <Stat label="Total Agents" value={agents.length} icon={UserCheck} />
                <Stat label="Today’s Revenue" value={`£${rev.today.toFixed(2)}`} icon={PoundSterling} tone="bg-[#1F0068] text-white" />
                <Stat label="Monthly Revenue" value={`£${rev.month.toFixed(2)}`} icon={PoundSterling} tone="bg-[#FF5C39] text-white" />
                <Stat label="Yearly Revenue" value={`£${rev.year.toFixed(2)}`} icon={PoundSterling} />
                <Stat label="Pending Payments" value={pendingPayments.length} icon={Bell} sub="awaiting bank transfer" />
                <Stat label="Pending Services" value={openServices.length} icon={Wrench} />
                <Stat label="Cancellations" value={cancellations.length} icon={XCircle} />
                <Stat label="Today’s Quotes" value={todaysQuotes.length} icon={FileText} />
                <Stat label="Outstanding Follow-ups" value={followUps.length} icon={Clock} sub=">24h since paperwork" />
              </div>

              <div className="mt-8 grid lg:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl border border-[#1F0068]/5 p-5">
                  <div className="flex items-center justify-between mb-3"><h3 className="font-bold text-[#1F0068]">Latest quotes</h3><span className="text-xs text-[#1F0068]/60">Last 5</span></div>
                  <div className="space-y-2">
                    {policies.slice(0, 5).map((p) => (
                      <div key={p.id} className="flex items-center justify-between text-sm py-2 border-b border-[#1F0068]/5 last:border-0">
                        <div><div className="font-semibold text-[#1F0068]">{p.title} {p.firstName} {p.lastName}</div><div className="text-xs text-[#1F0068]/60">{p.planNumber} · {new Date(p.createdAt).toLocaleDateString('en-GB')}</div></div>
                        <div className="text-right"><div className="font-bold text-[#1F0068]">£ {Number(p.amount).toFixed(2)}</div><div className="text-[10px] uppercase font-bold text-[#FF5C39]">{p.paymentStatus}</div></div>
                      </div>
                    ))}
                    {policies.length === 0 && <div className="text-sm text-[#1F0068]/60 py-6 text-center">No policies yet.</div>}
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-[#1F0068]/5 p-5">
                  <div className="flex items-center justify-between mb-3"><h3 className="font-bold text-[#1F0068]">Follow-ups needed</h3><span className="text-xs text-[#FF5C39] font-bold">{followUps.length} open</span></div>
                  <div className="space-y-2">
                    {followUps.slice(0, 5).map((p) => (
                      <div key={p.id} className="flex items-center justify-between text-sm py-2 border-b border-[#1F0068]/5 last:border-0">
                        <div><div className="font-semibold text-[#1F0068]">{p.firstName} {p.lastName}</div><div className="text-xs text-[#1F0068]/60">{p.planNumber}</div></div>
                        <span className="text-[10px] font-bold uppercase text-[#FF5C39]">{p.paymentStatus}</span>
                      </div>
                    ))}
                    {followUps.length === 0 && <div className="text-sm text-[#1F0068]/60 py-6 text-center">Everything is up to date.</div>}
                  </div>
                </div>
              </div>
            </>
          )}

          {tab === 'customers' && (
            <>
              <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                <h1 className="text-3xl font-bold text-[#1F0068] tracking-tight">Customers ({customers.length})</h1>
                <Button onClick={() => exportCSV(customers.map((c) => ({ name: c.name, email: c.email, mobile: c.mobile, city: c.city, postcode: c.postcode, status: c.status })), 'customers.csv')} variant="outline" className="rounded-full gap-1.5"><Download className="w-4 h-4" /> Export CSV</Button>
              </div>
              <div className="bg-white rounded-2xl border border-[#1F0068]/5 overflow-hidden">
                <div className="p-4 border-b border-[#1F0068]/5">
                  <div className="relative"><Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, postcode…" className="h-11 pl-10 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" /></div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#FFF6EB] text-[#1F0068]/70 text-xs uppercase tracking-wider">
                      <tr><th className="text-left px-5 py-3">Name</th><th className="text-left px-5 py-3">Email</th><th className="text-left px-5 py-3">Mobile</th><th className="text-left px-5 py-3">City</th><th className="text-left px-5 py-3">Postcode</th><th className="text-left px-5 py-3">Status</th><th className="text-right px-5 py-3">Actions</th></tr>
                    </thead>
                    <tbody className="divide-y divide-[#1F0068]/5">
                      {filteredCustomers.map((c) => (
                        <tr key={c.id || c.email} className="hover:bg-[#FFF6EB]">
                          <td className="px-5 py-3 font-semibold text-[#1F0068]">{c.name || `${c.firstName} ${c.lastName}`}</td>
                          <td className="px-5 py-3 text-[#1F0068]/80">{c.email}</td>
                          <td className="px-5 py-3 text-[#1F0068]/80">{c.mobile || '—'}</td>
                          <td className="px-5 py-3 text-[#1F0068]/80">{c.city || '—'}</td>
                          <td className="px-5 py-3 text-[#1F0068]/80">{c.postcode || '—'}</td>
                          <td className="px-5 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${c.status === 'Active' ? 'bg-[#00b67a]/15 text-[#00b67a]' : 'bg-[#1F0068]/10 text-[#1F0068]/70'}`}>{c.status}</span></td>
                          <td className="px-5 py-3 text-right">
                            {c.status === 'Active' ? (
                              <Button size="sm" variant="outline" onClick={() => { deactivateCustomer(c.id); toast({ title: 'Customer deactivated' }); }} className="rounded-full text-xs"><Ban className="w-3 h-3 mr-1" /> Deactivate</Button>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => { activateCustomer(c.id); toast({ title: 'Customer reactivated' }); }} className="rounded-full text-xs"><CheckCircle2 className="w-3 h-3 mr-1" /> Reactivate</Button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {filteredCustomers.length === 0 && <tr><td colSpan="7" className="px-5 py-10 text-center text-[#1F0068]/60">No customers found</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {tab === 'agents' && <AdminAgents />}

          {tab === 'emails' && <AdminEmails />}

          {tab === 'revenue' && (
            <>
              <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                <h1 className="text-3xl font-bold text-[#1F0068] tracking-tight">Revenue</h1>
                <Button data-testid="export-paid-policies" onClick={() => {
                  const rows = [];
                  policies.forEach((p) => {
                    const partials = Array.isArray(p.partialPayments) ? p.partialPayments : [];
                    const partialsSum = partials.reduce((s, pp) => s + Number(pp.amount || 0), 0);
                    partials.forEach((pp, i) => rows.push({
                      kind: 'partial', planNumber: p.planNumber, name: `${p.firstName} ${p.lastName}`, amount: Number(pp.amount || 0),
                      paidAt: pp.at, note: pp.note || `Partial payment ${i + 1}`,
                    }));
                    if (p.paymentStatus === 'Paid') {
                      const paidEvt = p.paymentEvents?.find((e) => e.status === 'Paid');
                      const remainder = Math.max(0, +(Number(p.amount || 0) - partialsSum).toFixed(2));
                      if (remainder > 0) rows.push({
                        kind: 'premium', planNumber: p.planNumber, name: `${p.firstName} ${p.lastName}`, amount: remainder,
                        paidAt: paidEvt?.at || '',
                      });
                    }
                    if (p.cancellation?.paidAt) rows.push({
                      kind: 'cancellation', planNumber: p.planNumber, name: `${p.firstName} ${p.lastName}`, amount: p.cancellation.amount,
                      paidAt: p.cancellation.paidAt,
                    });
                  });
                  exportCSV(rows, 'revenue.csv');
                }} variant="outline" className="rounded-full gap-1.5"><Download className="w-4 h-4" /> Export</Button>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat label="Today" value={`£${rev.today.toFixed(2)}`} icon={PoundSterling} tone="bg-white text-[#1F0068]" />
                <Stat label="This Week" value={`£${rev.week.toFixed(2)}`} icon={PoundSterling} tone="bg-white text-[#1F0068]" />
                <Stat label="This Month" value={`£${rev.month.toFixed(2)}`} icon={PoundSterling} tone="bg-[#1F0068] text-white" />
                <Stat label="This Year" value={`£${rev.year.toFixed(2)}`} icon={PoundSterling} tone="bg-[#FF5C39] text-white" />
              </div>

              <div className="mt-6 grid lg:grid-cols-3 gap-4">
                <div data-testid="chart-revenue-monthly" className="lg:col-span-2 bg-white rounded-2xl border border-[#1F0068]/5 p-5">
                  <h3 className="font-bold text-[#1F0068] mb-3">Monthly revenue (last 12 months)</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#1F0068" stopOpacity={0.5} />
                          <stop offset="100%" stopColor="#1F0068" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="canGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#FF5C39" stopOpacity={0.5} />
                          <stop offset="100%" stopColor="#FF5C39" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1F006810" />
                      <XAxis dataKey="label" stroke="#1F006880" fontSize={12} />
                      <YAxis stroke="#1F006880" fontSize={12} tickFormatter={(v) => `£${v}`} />
                      <Tooltip formatter={(v) => `£${Number(v).toFixed(2)}`} contentStyle={{ borderRadius: 12, border: '1px solid #1F006820' }} />
                      <Legend />
                      <Area type="monotone" dataKey="premium" name="Premium revenue" stroke="#1F0068" fill="url(#revGrad)" strokeWidth={2} />
                      <Area type="monotone" dataKey="cancellation" name="Cancellation revenue" stroke="#FF5C39" fill="url(#canGrad)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white rounded-2xl border border-[#1F0068]/5 p-5">
                  <h3 className="font-bold text-[#1F0068] mb-3">Revenue mix</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Premiums', value: policies.reduce((s, p) => {
                            const partials = Array.isArray(p.partialPayments) ? p.partialPayments : [];
                            const partialsSum = partials.reduce((a, pp) => a + Number(pp.amount || 0), 0);
                            if (p.paymentStatus === 'Paid') return s + Number(p.amount || 0);
                            return s + partialsSum;
                          }, 0) },
                          { name: 'Cancellations', value: policies.reduce((s, p) => s + (p.cancellation?.paidAt ? Number(p.cancellation.amount || 0) : 0), 0) },
                        ]}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={4}
                      >
                        <Cell fill="#1F0068" />
                        <Cell fill="#FF5C39" />
                      </Pie>
                      <Tooltip formatter={(v) => `£${Number(v).toFixed(2)}`} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="text-center text-xs text-[#1F0068]/60 -mt-3">Total: £{rev.total.toFixed(2)}</div>
                </div>
              </div>

              <div className="mt-6 bg-white rounded-2xl border border-[#1F0068]/5 p-5">
                <h3 className="font-bold text-[#1F0068] mb-3">All paid transactions</h3>
                <div data-testid="admin-revenue-transactions" className="space-y-1 max-h-80 overflow-y-auto pr-1">
                  {(() => {
                    const rows = [];
                    policies.forEach((p) => {
                      const partials = Array.isArray(p.partialPayments) ? p.partialPayments : [];
                      const partialsSum = partials.reduce((s, pp) => s + Number(pp.amount || 0), 0);
                      partials.forEach((pp, i) => rows.push({
                        key: `${p.id}-pp-${i}`,
                        name: `${p.firstName} ${p.lastName}`,
                        label: `${p.planNumber} · partial payment`,
                        amount: Number(pp.amount || 0),
                        at: pp.at,
                      }));
                      if (p.paymentStatus === 'Paid') {
                        const remainder = Math.max(0, +(Number(p.amount || 0) - partialsSum).toFixed(2));
                        if (remainder > 0) {
                          const paidEvt = p.paymentEvents?.find((e) => e.status === 'Paid');
                          rows.push({
                            key: `${p.id}-prem`,
                            name: `${p.firstName} ${p.lastName}`,
                            label: `${p.planNumber} · ${partials.length > 0 ? 'final balance' : 'premium'}`,
                            amount: remainder,
                            at: paidEvt?.at || p.createdAt,
                          });
                        }
                      }
                      if (p.cancellation?.paidAt) rows.push({
                        key: `${p.id}-cx`,
                        name: `${p.firstName} ${p.lastName}`,
                        label: `${p.planNumber} · cancellation settlement`,
                        amount: Number(p.cancellation.amount || 0),
                        at: p.cancellation.paidAt,
                        cx: true,
                      });
                    });
                    rows.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
                    if (rows.length === 0) return <div className="text-sm text-[#1F0068]/60 py-8 text-center">No revenue yet.</div>;
                    return rows.map((r) => (
                      <div key={r.key} className="flex items-center justify-between py-2 border-b border-[#1F0068]/5 last:border-0 text-sm">
                        <div className="font-semibold text-[#1F0068]">{r.name} <span className={`${r.cx ? 'text-[#FF5C39]/80' : 'text-[#1F0068]/50'} text-xs`}>· {r.label}</span></div>
                        <div className="font-bold text-[#00b67a]">+ £{r.amount.toFixed(2)}</div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </>
          )}

          {tab === 'services' && (
            <>
              <h1 className="text-3xl font-bold text-[#1F0068] tracking-tight mb-6">Service requests</h1>
              <div className="grid sm:grid-cols-4 gap-3 mb-6">
                <Stat label="Open" value={openServices.length} icon={Wrench} />
                <Stat label="Engineer required" value={services.filter((s) => s.needsEngineer).length} icon={Wrench} />
                <Stat label="Completed" value={services.filter((s) => s.status === 'Completed').length} icon={CheckCircle2} />
                <Stat label="Cancelled" value={services.filter((s) => s.status === 'Cancelled').length} icon={XCircle} />
              </div>
              <div data-testid="chart-services-monthly" className="bg-white rounded-2xl border border-[#1F0068]/5 p-5">
                <h3 className="font-bold text-[#1F0068] mb-3">Service requests (last 12 months)</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1F006810" />
                    <XAxis dataKey="label" stroke="#1F006880" fontSize={12} />
                    <YAxis stroke="#1F006880" fontSize={12} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #1F006820' }} />
                    <Legend />
                    <Bar dataKey="services" name="Service requests" fill="#1F0068" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="quotes" name="Quotes created" fill="#FFB7A1" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {services.length === 0 && <div className="mt-4 bg-white rounded-2xl border border-[#1F0068]/5 p-6 text-center text-[#1F0068]/60 text-sm">No service requests yet — engineer bookings from customers will appear here.</div>}
            </>
          )}

          {tab === 'cancellations' && (
            <>
              <h1 className="text-3xl font-bold text-[#1F0068] tracking-tight mb-6">Cancellations</h1>
              {(() => {
                const cx = policies.filter((p) => p.cancellation);
                const paidCount = cx.filter((p) => p.cancellation?.paidAt).length;
                const cxRevenue = cx.reduce((s, p) => s + (p.cancellation?.paidAt ? Number(p.cancellation.amount || 0) : 0), 0);
                const cxDue = cx.reduce((s, p) => s + (!p.cancellation?.paidAt ? Number(p.cancellation?.amount || 0) : 0), 0);
                return (
                  <>
                    <div className="grid sm:grid-cols-4 gap-3 mb-6">
                      <Stat label="Total cancellations" value={cx.length} icon={XCircle} />
                      <Stat label="Settlements paid" value={paidCount} icon={CheckCircle2} tone="bg-[#00b67a]/10 text-[#00b67a]" />
                      <Stat label="Cancellation revenue" value={`£${cxRevenue.toFixed(2)}`} icon={PoundSterling} tone="bg-[#1F0068] text-white" />
                      <Stat label="Awaiting settlement" value={`£${cxDue.toFixed(2)}`} icon={PoundSterling} tone="bg-[#FF5C39] text-white" />
                    </div>
                    <div data-testid="chart-cancellations-monthly" className="bg-white rounded-2xl border border-[#1F0068]/5 p-5">
                      <h3 className="font-bold text-[#1F0068] mb-3">Cancellations vs settlement revenue (last 12 months)</h3>
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={series}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1F006810" />
                          <XAxis dataKey="label" stroke="#1F006880" fontSize={12} />
                          <YAxis yAxisId="left" stroke="#1F006880" fontSize={12} allowDecimals={false} />
                          <YAxis yAxisId="right" orientation="right" stroke="#FF5C39" fontSize={12} tickFormatter={(v) => `£${v}`} />
                          <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #1F006820' }} />
                          <Legend />
                          <Line yAxisId="left" type="monotone" dataKey="cancelled" name="Cancellations (count)" stroke="#1F0068" strokeWidth={3} dot={{ r: 4, fill: '#1F0068' }} />
                          <Line yAxisId="right" type="monotone" dataKey="cancellation" name="Settlement revenue (£)" stroke="#FF5C39" strokeWidth={3} dot={{ r: 4, fill: '#FF5C39' }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="mt-6 bg-white rounded-2xl border border-[#1F0068]/5 overflow-hidden">
                      <div className="p-4 border-b border-[#1F0068]/5 flex items-center justify-between"><h3 className="font-bold text-[#1F0068]">Cancellation log</h3><span className="text-xs text-[#1F0068]/60">{cx.length} total</span></div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-[#FFF6EB] text-[#1F0068]/70 text-xs uppercase tracking-wider">
                            <tr><th className="text-left px-5 py-3">Customer</th><th className="text-left px-5 py-3">Plan</th><th className="text-left px-5 py-3">Cancelled</th><th className="text-left px-5 py-3">Amount</th><th className="text-left px-5 py-3">Reason</th><th className="text-left px-5 py-3">Status</th></tr>
                          </thead>
                          <tbody className="divide-y divide-[#1F0068]/5">
                            {cx.map((p) => (
                              <tr key={p.id} className="hover:bg-[#FFF6EB]">
                                <td className="px-5 py-3 font-semibold text-[#1F0068]">{p.firstName} {p.lastName}</td>
                                <td className="px-5 py-3 text-[#1F0068]/80">{p.planNumber}</td>
                                <td className="px-5 py-3 text-[#1F0068]/80">{new Date(p.cancellation.cancelledAt).toLocaleDateString('en-GB')}</td>
                                <td className="px-5 py-3 font-bold text-[#FF5C39]">£ {Number(p.cancellation.amount || 0).toFixed(2)}</td>
                                <td className="px-5 py-3 text-[#1F0068]/80">{p.cancellation.reason || '—'}</td>
                                <td className="px-5 py-3">
                                  {p.cancellation.paidAt
                                    ? <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-[#00b67a]/15 text-[#00b67a]">Paid · {new Date(p.cancellation.paidAt).toLocaleDateString('en-GB')}</span>
                                    : <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-[#FF5C39]/15 text-[#FF5C39]">Awaiting</span>}
                                </td>
                              </tr>
                            ))}
                            {cx.length === 0 && <tr><td colSpan="6" className="px-5 py-10 text-center text-[#1F0068]/60">No cancellations yet.</td></tr>}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                );
              })()}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
